import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  Button,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import YouTubeIcon from '@mui/icons-material/YouTube';

// Custom Auto-playing Slider Card Component for Australia
const SliderCard = ({ item, onViewMore }) => {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % item.images.length);
    }, 3500);
    return () => clearInterval(timer);
  }, [item.images.length]);

  return (
    <Card
      sx={{
        height: { xs: 260, sm: 300, md: 340 },
        borderRadius: 4,
        position: 'relative',
        overflow: 'hidden',
        boxShadow: 'none',
      }}
    >
      {item.images.map((img, idx) => (
        <Box
          key={idx}
          sx={{
            position: 'absolute',
            inset: 0,
            opacity: idx === current ? 1 : 0,
            transition: 'opacity 1s ease-in-out',
            backgroundImage: `url(${img})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
      ))}
      <Box
        sx={{
          position: 'relative',
          zIndex: 2,
          p: 4,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          background: 'linear-gradient(to right, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 60%)',
          color: 'white',
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          {item.title}
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
          {item.subtitle}
        </Typography>

        <Button
          variant="contained"
          endIcon={<ArrowForwardIcon sx={{ transform: 'rotate(-45deg)', fontSize: '18px !important' }} />}
          onClick={onViewMore}
          sx={{
            bgcolor: '#0C2340',
            color: 'white',
            borderRadius: 50,
            px: 2.5,
            py: 0.8,
            textTransform: 'none',
            fontWeight: 700,
            '&:hover': { bgcolor: '#f0f0f0' },
          }}
        >
          View More
        </Button>
      </Box>
      {/* Slider Indicator Dots */}
      <Box sx={{ position: 'absolute', bottom: 24, left: 32, display: 'flex', gap: 1, zIndex: 2 }}>
        {item.images.map((_, idx) => (
          <Box
            key={idx}
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              bgcolor: idx === current ? '#FFC800' : 'rgba(255,255,255,0.6)',
              transition: 'background-color 0.3s ease',
            }}
          />
        ))}
      </Box>
    </Card>
  );
};

const TravelSpecials = () => {
  const navigate = useNavigate();
  const specialsData = [
    {
      id: 'kedarnath',
      type: 'hero',
      title: 'Kedarnath',
      subtitle: 'Plan your journey Now',
      image: 'https://images.unsplash.com/photo-1612438214708-f428a707dd4e?q=80&w=1074&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
      col: { xs: 12, md: 4 },
    },
    {
      id: 'australia',
      type: 'slider',
      title: 'Australia',
      subtitle: 'Wonder Without Limits.',
      images: [
        'https://images.unsplash.com/photo-1506973035872-a4ec16b8e8d9?auto=format&fit=crop&w=1200&q=80',
        'https://images.unsplash.com/photo-1523482580672-f109ba8cb9be?auto=format&fit=crop&w=1200&q=80',
      ],
      col: { xs: 12, md: 8 },
    },
    {
      id: 'video',
      type: 'video',
      title: 'Book your holiday in minutes just the way you like it with Travel Specials',
      thumbnail: 'https://images.unsplash.com/photo-1542314831-068cd1dbfeeb?auto=format&fit=crop&w=800&q=80',
      col: { xs: 12, md: 6 },
    },
    {
      id: 'itinerary',
      type: 'card-light',
      title: 'Build Your Own Itinerary!',
      subtitle: 'Customize your flights,hotels & sightseeing in just 10 minutes',
      image: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=800&q=80',
      bgColor: '#F0F2F5',
      col: { xs: 12, md: 3 },
    },
    {
      id: 'durgapuja',
      type: 'card-color',
      title: 'Durga Puja',
      subtitle: 'Special Tour Packages',
      image: 'https://images.unsplash.com/photo-1587474260584-136574528ed5?auto=format&fit=crop&w=800&q=80',
      bgColor: '#FFC107',
      col: { xs: 12, md: 3 },
    },
  ];

  return (
    <Box sx={{ py: 6, bgcolor: '#FFFFFF', px: { xs: 2, md: 4 } }}>
      <Container maxWidth="xl">
        <Typography variant="h4" sx={{ fontWeight: 800, mb: 3, color: '#1a1a1a' }}>
          Travel Specials
        </Typography>

        <Grid container spacing={2}>
          {specialsData.map((item) => (
            <Grid item xs={item.col.xs} md={item.col.md} key={item.id}>

              {/* SLIDER CARD - AUSTRALIA */}
              {item.type === 'slider' && (
                <SliderCard
                  item={item}
                  onViewMore={() => navigate('/tours?search=Australia')}
                />
              )}

              {/* HERO CARD - KEDARNATH */}
              {item.type === 'hero' && (
                <Card
                  sx={{
                    height: { xs: 260, sm: 300, md: 340 },
                    borderRadius: 4,
                    position: 'relative',
                    overflow: 'hidden',
                    boxShadow: 'none',
                    backgroundImage: `url(${item.image})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      background: 'linear-gradient(to bottom, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0) 100%)',
                      p: 4,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      color: 'white',
                    }}
                  >
                    <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                      {item.title}
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 3 }}>
                      {item.subtitle}
                    </Typography>
                    <Button
                      variant="contained"
                      endIcon={<ArrowForwardIcon sx={{ transform: 'rotate(-45deg)', fontSize: '18px !important' }} />}
                      onClick={() => navigate('/tours?search=Kedarnath')}
                      sx={{
                        bgcolor: '#0C2340',
                        color: 'white',
                        borderRadius: 50,
                        px: 2.5,
                        py: 0.8,
                        textTransform: 'none',
                        fontWeight: 700,
                        '&:hover': { bgcolor: '#f0f0f0' },
                      }}
                    >
                      View More
                    </Button>
                  </Box>
                </Card>
              )}

              {/* VIDEO CARD */}
              {item.type === 'video' && (
                <Card
                  sx={{
                    height: { xs: 260, sm: 300, md: 340 },
                    borderRadius: 4,
                    position: 'relative',
                    overflow: 'hidden',
                    bgcolor: 'black',
                    boxShadow: 'none',
                  }}
                >
                  <Box
                    component="img"
                    src={item.thumbnail}
                    sx={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.6 }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      p: 3,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                      <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 0.5 }}>
                        <Typography variant="caption" sx={{ color: '#0052CC', fontWeight: 800 }}>TS</Typography>
                      </Box>
                      <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, lineHeight: 1.3, maxWidth: '85%' }}>
                        {item.title}
                      </Typography>
                    </Box>

                    {/* Play Button */}
                    <Box
                      sx={{ alignSelf: 'center', cursor: 'pointer' }}
                      onClick={() => window.open('https://www.youtube.com/watch?v=example', '_blank')}
                    >
                      <Box sx={{ bgcolor: '#FF0000', borderRadius: 2, px: 4, py: 1.5, display: 'inline-flex' }}>
                        <PlayArrowIcon sx={{ color: 'white', fontSize: 40 }} />
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end' }}>
                      {/* Watch on YouTube Button */}
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'rgba(0,0,0,0.8)', px: 2, py: 1, borderRadius: 5, cursor: 'pointer' }}
                        onClick={() => window.open('https://www.youtube.com/results?search_query=travel+specials', '_blank')}
                      >
                        <Typography variant="body2" sx={{ color: 'white', fontWeight: 600 }}>Watch on</Typography>
                        <YouTubeIcon sx={{ color: 'white', fontSize: 24 }} />
                      </Box>
                    </Box>
                  </Box>
                </Card>
              )}

              {/* SMALL CARDS (Itinerary & Durga Puja) */}
              {(item.type === 'card-light' || item.type === 'card-color') && (
                <Card
                  onClick={() => {
                    if (item.id === 'durgapuja') navigate('/tours?category=Religious&search=Durga+Puja');
                    else if (item.id === 'itinerary') navigate('/booking');
                  }}
                  sx={{
                    height: { xs: 260, sm: 300, md: 340 },
                    borderRadius: 4,
                    display: 'flex',
                    flexDirection: 'column',
                    bgcolor: item.bgColor,
                    position: 'relative',
                    overflow: 'hidden',
                    boxShadow: 'none',
                    cursor: 'pointer',
                  }}
                >
                  <Box sx={{ p: 3, position: 'relative', zIndex: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 800, mb: 1, color: '#1a1a1a', lineHeight: 1.2 }}>
                      {item.title}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 2, color: '#333' }}>
                      {item.subtitle}
                    </Typography>
                    <Box
                      onClick={(e) => {
                        e.stopPropagation();
                        if (item.id === 'durgapuja') navigate('/tours?category=Religious&search=Durga+Puja');
                        else if (item.id === 'itinerary') navigate('/booking');
                      }}
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        bgcolor: '#0052CC',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        '&:hover': { bgcolor: '#0044AA' },
                      }}
                    >
                      <ArrowForwardIcon sx={{ color: 'white', fontSize: 20, transform: 'rotate(-45deg)' }} />
                    </Box>
                  </Box>
                  <Box sx={{ flex: 1, position: 'relative', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
                    <Box
                      component="img"
                      src={item.image}
                      sx={{
                        width: '90%',
                        maxHeight: '100%',
                        objectFit: 'contain',
                        borderBottomLeftRadius: 16,
                        borderBottomRightRadius: 16,
                      }}
                    />
                  </Box>
                </Card>
              )}

            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default TravelSpecials;